import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Printer, X } from "lucide-react";
import { useEffect, useState } from "react";

interface VisualizadorArquivoProps {
  arquivoUrl: string;
  nomeArquivo: string;
  tipoArquivo?: string;
  open: boolean;
  onClose: () => void;
}

export const VisualizadorArquivo = ({
  arquivoUrl,
  nomeArquivo,
  tipoArquivo,
  open,
  onClose,
}: VisualizadorArquivoProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determinar se é imagem ou PDF
  const isImagem = tipoArquivo
    ? ["jpg", "jpeg", "png", "gif", "webp"].includes(tipoArquivo.toLowerCase())
    : /\.(jpg|jpeg|png|gif|webp)$/i.test(nomeArquivo);

  const isPDF = tipoArquivo
    ? tipoArquivo.toLowerCase() === "pdf"
    : /\.pdf$/i.test(nomeArquivo);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
    }
  }, [open, arquivoUrl]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = arquivoUrl;
    link.download = nomeArquivo;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (isPDF) {
      // Para PDF, abrir em nova janela e imprimir
      const printWindow = window.open(arquivoUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } else if (isImagem) {
      // Para imagens, criar uma nova janela com a imagem
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>${nomeArquivo}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                }
                img {
                  max-width: 100%;
                  max-height: 100vh;
                  object-fit: contain;
                }
              </style>
            </head>
            <body>
              <img src="${arquivoUrl}" alt="${nomeArquivo}" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError("Erro ao carregar a imagem");
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 [&>button]:hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold truncate flex-1 mr-4">
              {nomeArquivo}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="gap-2"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="relative flex-1 overflow-auto p-6 bg-gray-50 dark:bg-gray-900">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-sm text-muted-foreground">
                  Carregando arquivo...
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-destructive mb-4">{error}</p>
                <Button variant="outline" onClick={onClose}>
                  Fechar
                </Button>
              </div>
            </div>
          )}

          {!error && (
            <>
              {isImagem && (
                <div className="flex items-center justify-center min-h-[400px]">
                  <img
                    src={arquivoUrl}
                    alt={nomeArquivo}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: loading ? "none" : "block" }}
                  />
                </div>
              )}

              {isPDF && (
                <div className="w-full h-[70vh]">
                  <iframe
                    src={`${arquivoUrl}#toolbar=1`}
                    className="w-full h-full border-0 rounded-lg shadow-lg"
                    title={nomeArquivo}
                    onLoad={handleIframeLoad}
                    style={{ display: loading ? "none" : "block" }}
                  />
                </div>
              )}

              {!isImagem && !isPDF && (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <p className="text-muted-foreground mb-4">
                      Visualização não disponível para este tipo de arquivo
                    </p>
                    <Button variant="outline" onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      Baixar arquivo
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisualizadorArquivo;

